import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getClearPOIS, getRichMapLoaded } from '../../../../../store/uislice';
import { POI_TYPES_ORIGINAL } from '../../util';
import { closeRoomDetailsPopup } from '../room-details-popup';
const PANEL_ITEMS = [
    {
        labelKey: 'floor.poi.cafeteria',
        image: require('../../../../../assets/images/poi-1.svg'),
        type: 'restaurant',
        storeKey: 'Cafeteria',
    },
    {
        labelKey: 'floor.poi.rest-rooms',
        image: require('../../../../../assets/images/poi-2.svg'),
        type: 'toilets',
        storeKey: 'Rest Rooms',
    },
    {
        labelKey: 'floor.poi.help-desk',
        image: require('../../../../../assets/images/poi-5.svg'),
        type: 'help-desk',
        storeKey: 'Help Desk',
    },
    {
        labelKey: 'floor.poi.child-care-area',
        image: require('../../../../../assets/images/poi-6.svg'),
        type: 'child-care',
        storeKey: 'Child Care Area',
    },
    {
        labelKey: 'floor.poi.stairs',
        image: require('../../../../../assets/images/poi-3.svg'),
        type: 'stairs',
        storeKey: 'Stairs',
    },
    {
        labelKey: 'floor.poi.elevators',
        image: require('../../../../../assets/images/poi-4.svg'),
        type: 'lift',
        storeKey: 'Elevators',
    },
];
const Panel = styled.div `
  width: clamp(8.35rem, 8.8vw, 9.3rem);
  margin-top: 0;
  margin-left: clamp(0.3rem, 0.35vw, 0.45rem);
  padding: clamp(0.8rem, 0.95vw, 0.95rem) clamp(0.75rem, 0.82vw, 0.8rem);
  border-radius: clamp(1rem, 1.05vw, 1.05rem);
  background: linear-gradient(
    180deg,
    rgba(33, 40, 49, 0.96) 0%,
    rgba(28, 35, 43, 0.96) 100%
  );
  box-shadow:
    inset 0 1px 0 rgba(104, 120, 141, 0.22),
    0 16px 34px rgba(0, 0, 0, 0.28);
  flex: 0 0 auto;
`;
const Title = styled.div `
  color: rgba(186, 195, 206, 0.78);
  font-family: 'Sora', sans-serif;
  font-size: clamp(0.76rem, 0.88vw, 0.88rem);
  font-weight: 400;
  line-height: 1.2;
  margin-bottom: clamp(0.8rem, 0.95vw, 0.95rem);
`;
const Grid = styled.div `
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: clamp(0.8rem, 0.95vw, 0.95rem);
  row-gap: clamp(0.85rem, 1vw, 1rem);
`;
const Item = styled.button `
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0;
  color: inherit;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-width: 0;
`;
const Tile = styled.div `
  width: 100%;
  max-width: clamp(3.8rem, 4.2vw, 4.25rem);
  aspect-ratio: 1;
  flex-shrink: 0;
  border-radius: clamp(0.95rem, 1vw, 1rem);
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #556277 0%, #4e5b6f 100%);
  box-shadow:
    inset 0 1px 0 rgba(126, 143, 166, 0.34),
    0 8px 18px rgba(0, 0, 0, 0.28);
  transition:
    transform 160ms ease,
    background 160ms ease,
    box-shadow 160ms ease;

  ${({ $selected }) => $selected &&
    css `
      background: linear-gradient(180deg, #7b8ca4 0%, #64758d 100%);
      outline: 2px solid rgba(155, 196, 255, 0.75);
      outline-offset: -1px;
      box-shadow:
        inset 0 1px 0 rgba(154, 170, 191, 0.36),
        0 0 0 3px rgba(53, 116, 255, 0.18),
        0 12px 24px rgba(0, 0, 0, 0.34);
      transform: translateY(-2px) scale(1.02);
    `}
`;
const Icon = styled.img `
  width: clamp(1.02rem, 1.18vw, 1.22rem);
  height: clamp(1.02rem, 1.18vw, 1.22rem);
`;
const Label = styled.div `
  margin-top: clamp(0.38rem, 0.45vw, 0.45rem);
  color: #f4f7fb;
  font-family: 'Sora', sans-serif;
  font-size: clamp(0.56rem, 0.62vw, 0.62rem);
  font-weight: 600;
  line-height: 1.2;
  text-align: center;
  min-height: 1.5em;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const POIsPanel = ({ floorNumber }) => {
    const { t } = useTranslation();
    const [selectedPOIType, setSelectedPOIType] = useState('');
    const clearPOIs = useSelector(getClearPOIS);
    const richMapLoaded = useSelector(getRichMapLoaded);
    const richMap = window.rMap;
    // Types the SDK reports as present on the current floor. `null` = not resolved
    // yet (rich map/POI manifest still loading); `[]` = resolved, floor has none.
    const [floorPoiTypes, setFloorPoiTypes] = useState(null);
    // Source of truth for which POIs exist on a floor is the SDK's per-floor POI
    // manifest, read via rMap.getPOIsForFloor(floorNum). Unlike getFeatures()
    // (a cross-floor, lazily-populated cache), this is scoped to the given floor,
    // so it matches the SDK's own legend exactly. The manifest is loaded once at
    // init, so on floor change we just re-query; we still retry while the map is
    // starting up in case the panel mounts before the manifest is ready.
    useEffect(() => {
        if (floorNumber === undefined ||
            floorNumber === null ||
            Number.isNaN(floorNumber)) {
            setFloorPoiTypes(null);
            return;
        }
        let cancelled = false;
        let attempts = 0;
        let timerId;
        const readFloorPois = () => {
            if (cancelled)
                return;
            const map = window.rMap;
            const types = typeof (map === null || map === void 0 ? void 0 : map.getPOIsForFloor) === 'function'
                ? map.getPOIsForFloor(Number(floorNumber))
                : null;
            if (types != null) {
                setFloorPoiTypes(Array.isArray(types) ? types : []);
                return;
            }
            // Manifest not ready yet: retry a bounded number of times.
            attempts += 1;
            if (attempts <= 20) {
                timerId = setTimeout(readFloorPois, 300);
            }
        };
        readFloorPois();
        return () => {
            cancelled = true;
            if (timerId)
                clearTimeout(timerId);
        };
    }, [floorNumber, richMapLoaded]);
    const availableItems = useMemo(() => {
        if (floorPoiTypes == null)
            return [];
        return PANEL_ITEMS.filter((item) => floorPoiTypes.includes(item.type));
    }, [floorPoiTypes]);
    const handlePOIClick = (type) => {
        setTimeout(() => {
            // Clear any existing POI highlights explicitly rather than calling
            // richMap.reset(): reset() flies the camera back to the initial
            // position/zoom, which recenters the map on every POI toggle. The
            // per-type clear below de-highlights POIs without touching the camera.
            closeRoomDetailsPopup();
            for (const originalType of POI_TYPES_ORIGINAL) {
                richMap === null || richMap === void 0 ? void 0 : richMap.updatePoiSelection(originalType, true);
            }
        }, 100);
        if (type === selectedPOIType) {
            setSelectedPOIType('');
            return;
        }
        setSelectedPOIType(type);
        setTimeout(() => {
            if (richMap) {
                richMap.updatePoiSelection(type, false);
            }
        }, 100);
    };
    useEffect(() => {
        setTimeout(() => {
            for (const type of POI_TYPES_ORIGINAL) {
                richMap === null || richMap === void 0 ? void 0 : richMap.updatePoiSelection(type, true);
            }
        }, 100);
        setSelectedPOIType('');
    }, [clearPOIs, richMap]);
    // A floor may have no POIs (or the manifest isn't resolved yet); render
    // nothing rather than an empty widget so the panel truly reflects the floor.
    if (availableItems.length === 0) {
        return null;
    }
    return (_jsxs(Panel, { children: [_jsx(Title, { children: t('floor.map.label-poi-on-this-floor') }), _jsx(Grid, { children: availableItems.map((item) => (_jsxs(Item, Object.assign({ onClick: () => handlePOIClick(item.type), "aria-label": t(item.labelKey), title: t(item.labelKey) }, { children: [_jsx(Tile, Object.assign({ "$selected": selectedPOIType === item.type }, { children: _jsx(Icon, { src: item.image, alt: t(item.labelKey) }) })), _jsx(Label, { children: t(item.labelKey) })] }), item.type))) })] }));
};
export default POIsPanel;
